import React, { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowUpDown, Loader2, CheckCircle2, ArrowUp } from 'lucide-react';
import { EmptyState } from './EmptyState';

export interface ColumnDef<T> {
  header: string;
  accessorKey?: keyof T;
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  searchPlaceholder?: string;
  searchField?: keyof T;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  pageSize?: number;
  totalCount?: number;
  infiniteScroll?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  sentinelRef?: (node: HTMLElement | null) => void;
  hideSearch?: boolean;
  fixedHeight?: boolean;
  containerHeightClass?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  onClearFilters?: () => void;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  searchPlaceholder = 'Search records...',
  searchField,
  isLoading = false,
  onRowClick: _onRowClick,
  totalCount,
  infiniteScroll = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  sentinelRef,
  hideSearch = false,
  fixedHeight = false,
  containerHeightClass,
  emptyTitle = 'Data Not Found',
  emptyDescription = 'No records match your selected filter or search criteria.',
  onClearFilters,
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleContainerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setShowScrollTop(scrollTop > 80);
  };

  const scrollToTop = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Filter
  const filteredData = useMemo(() => {
    if (!searchTerm || infiniteScroll) return data;
    return data.filter((row) => {
      if (searchField) {
        const val = row[searchField];
        return val ? String(val).toLowerCase().includes(searchTerm.toLowerCase()) : false;
      }
      return Object.values(row).some((val) =>
        val ? String(val).toLowerCase().includes(searchTerm.toLowerCase()) : false
      );
    });
  }, [data, searchTerm, searchField, infiniteScroll]);

  // Sort
  const sortedData = useMemo(() => {
    if (!sortColumn || infiniteScroll) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal === bVal) return 0;
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;
      const comp = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? comp : -comp;
    });
  }, [filteredData, sortColumn, sortDirection, infiniteScroll]);

  // Continuous uninterrupted data stream (no breaking page slicing)
  const displayData = useMemo(() => {
    if (infiniteScroll) return data;
    return sortedData;
  }, [data, sortedData, infiniteScroll]);

  const totalEntries = totalCount !== undefined ? totalCount : data.length;

  const handleSort = (key?: keyof T) => {
    if (!key) return;
    if (sortColumn === key) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(key);
      setSortDirection('asc');
    }
  };

  return (
    <div className="w-full space-y-3">
      {/* Controls Header (Hidden if managed externally) */}
      {!hideSearch && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-4 text-sm font-medium placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="text-xs text-muted-foreground font-medium">
            Showing <span className="text-foreground font-semibold">{displayData.length}</span> of{' '}
            <span className="text-foreground font-semibold">{totalEntries}</span> matching records
          </div>
        </div>
      )}

      {/* Table Structure (Fixed scrollable container with locked sticky header) */}
      <div className="relative">
        <div
          ref={tableContainerRef}
          onScroll={handleContainerScroll}
          className={`overflow-x-auto custom-scrollbar rounded-xl border border-border bg-card shadow-xs ${
            containerHeightClass || (fixedHeight ? 'max-h-[520px] overflow-y-auto' : '')
          }`}
        >
          <table className="w-full text-left text-sm font-poppins">
          <thead className="bg-muted/80 backdrop-blur-sm border-b border-border sticky top-0 z-10 font-poppins">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className={`py-3.5 whitespace-nowrap text-left text-xs font-bold uppercase tracking-wider text-black dark:text-white align-middle ${
                    idx === 0
                      ? 'pl-4 pr-3'
                      : idx === columns.length - 1
                      ? 'pl-3 pr-8 min-w-[150px]'
                      : 'px-3'
                  }`}
                >
                  {col.sortable && col.accessorKey ? (
                    <button
                      onClick={() => handleSort(col.accessorKey)}
                      className="flex items-center gap-1.5 hover:text-primary font-bold text-black dark:text-white whitespace-nowrap"
                    >
                      <span className="whitespace-nowrap">{col.header}</span>
                      <ArrowUpDown className="h-3 w-3 opacity-70" />
                    </button>
                  ) : (
                    <span className="whitespace-nowrap font-bold text-black dark:text-white">{col.header}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, rIdx) => (
                <tr key={rIdx} className="animate-pulse">
                  {columns.map((_, cIdx) => (
                    <td key={cIdx} className="px-4 py-3">
                      <div className="h-4 w-3/4 rounded bg-muted/60" />
                    </td>
                  ))}
                </tr>
              ))
            ) : displayData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="p-0">
                  <EmptyState
                    title={emptyTitle}
                    description={emptyDescription}
                    actionLabel={onClearFilters ? 'Clear All Filters' : undefined}
                    onAction={onClearFilters}
                  />
                </td>
              </tr>
            ) : (
              <>
                <AnimatePresence mode="popLayout" initial={false}>
                  {displayData.map((row, rIdx) => {
                    const rowKey = row.id || row.requestNumber || rIdx;
                    return (
                      <motion.tr
                        key={rowKey}
                        layout="position"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{
                          opacity: 0,
                          scale: 0.92,
                          x: -60,
                          filter: 'blur(3px) grayscale(80%)',
                          backgroundColor: 'rgba(244, 63, 94, 0.18)',
                          transition: { duration: 0.32, ease: [0.4, 0, 0.2, 1] },
                        }}
                        className="transition-colors hover:bg-muted/40"
                      >
                        {columns.map((col, cIdx) => (
                          <td
                            key={cIdx}
                            className={`py-3.5 font-medium text-foreground align-middle font-poppins ${
                              cIdx === 0
                                ? 'pl-4 pr-3'
                                : cIdx === columns.length - 1
                                ? 'pl-3 pr-8 min-w-[150px]'
                                : 'px-3'
                            }`}
                          >
                            {col.cell
                              ? col.cell(row)
                              : col.accessorKey
                              ? String(row[col.accessorKey] ?? '-')
                              : '-'}
                          </td>
                        ))}
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>

                {/* Infinite Scroll Sentinel Row */}
                {infiniteScroll && (
                  <tr ref={sentinelRef} className="bg-muted/20">
                    <td colSpan={columns.length} className="py-4 text-center text-xs font-semibold text-muted-foreground">
                      {isFetchingNextPage ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                          <span>Loading more records...</span>
                        </div>
                      ) : hasNextPage ? (
                        <span>Scroll to load more</span>
                      ) : (
                        <div className="flex items-center justify-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span>All {totalEntries} matching records loaded</span>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Floating Scroll to Top Pill (Middle of the table when scrolling) */}
      {showScrollTop && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 animate-in fade-in zoom-in-95 duration-150 pointer-events-auto">
          <button
            type="button"
            onClick={scrollToTop}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground font-black text-xs shadow-2xl shadow-primary/40 hover:bg-primary/90 hover:scale-105 active:scale-95 transition-all cursor-pointer border border-white/20 backdrop-blur-md"
            title="Back to entry #1"
          >
            <ArrowUp className="h-4 w-4 stroke-[3]" />
            <span>Back to Top</span>
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
